import { useCallback, useEffect, useRef, useState } from 'react';
import { UploadAPI } from '../services/endpoints/media';
import { Button, Spinner } from './ui';
import './UploadDropzone.css';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_RETRIES = 3;

type PartState = 'pending' | 'in_flight' | 'done' | 'failed';

interface PartProgress {
  partNumber: number;
  signedUrl: string;
  state: PartState;
  attempts: number;
  etag?: string;
}

interface ResumePayload {
  uploadId: string;
  filename: string;
  fileType: string;
  fileSize: number;
  fileName: string;
  parts: { partNumber: number; etag?: string }[];
}

interface Props {
  mediaId: string;
  mediaType: 'AUDIO' | 'VIDEO';
  existingFilename?: string;
  onComplete: (filename: string) => void;
}

export function UploadDropzone({ mediaId, mediaType, existingFilename, onComplete }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [parts, setParts] = useState<PartProgress[]>([]);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'init' | 'uploading' | 'completing' | 'done' | 'error' | 'paused'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [resumeAvailable, setResumeAvailable] = useState<ResumePayload | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<File | null>(null);

  const resumeKey = `nb-upload:${mediaId}`;

  // Check for resumable upload on mount
  useEffect(() => {
    const raw = sessionStorage.getItem(resumeKey);
    if (raw) {
      try { setResumeAvailable(JSON.parse(raw) as ResumePayload); } catch {}
    }
  }, [resumeKey]);

  const validate = useCallback((f: File): string | null => {
    if (mediaType === 'AUDIO' && !f.type.startsWith('audio/')) return 'Expected an audio file.';
    if (mediaType === 'VIDEO' && !f.type.startsWith('video/')) return 'Expected a video file.';
    if (f.size === 0) return 'File is empty.';
    if (f.size > 4 * 1024 * 1024 * 1024) return 'File exceeds 4 GB cap.';
    return null;
  }, [mediaType]);

  function onPick(picked: File | undefined) {
    if (!picked) return;
    const err = validate(picked);
    if (err) { setError(err); return; }
    setError(null);
    setFile(picked);
    fileRef.current = picked;
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const dropped = e.dataTransfer.files;
    if (dropped.length > 1) { setError('Drop a single file.'); return; }
    onPick(dropped[0]);
  }

  async function uploadPart(part: PartProgress, blob: Blob, contentType: string, attempt = 1): Promise<string> {
    try {
      const ac = abortRef.current!;
      const etag = await UploadAPI.putPart(part.signedUrl, blob, contentType, ac.signal);
      return etag;
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      if (attempt >= MAX_RETRIES) throw err;
      await sleep(400 * 2 ** (attempt - 1));
      return uploadPart(part, blob, contentType, attempt + 1);
    }
  }

  async function start() {
    if (!file) return;
    setPhase('init');
    setError(null);
    const ac = new AbortController();
    abortRef.current = ac;

    const ext = file.name.includes('.') ? file.name.split('.').pop() : 'dat';
    const filename = `${mediaId}.${ext}`;
    const numberOfParts = Math.ceil(file.size / CHUNK_SIZE);

    try {
      const init = await UploadAPI.init({ filename, fileType: file.type, numberOfParts, mediaType });
      setUploadId(init.uploadId);
      const initial: PartProgress[] = init.partETags.map(p => ({
        partNumber: p.partNumber,
        signedUrl: p.signedUrl,
        state: 'pending',
        attempts: 0,
      }));
      setParts(initial);
      sessionStorage.setItem(resumeKey, JSON.stringify({
        uploadId: init.uploadId,
        filename,
        fileType: file.type,
        fileSize: file.size,
        fileName: file.name,
        parts: initial.map(p => ({ partNumber: p.partNumber })),
      } satisfies ResumePayload));

      setPhase('uploading');

      const done: { partNumber: number; etag: string }[] = [];

      for (const p of initial) {
        if (ac.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        setParts(prev => prev.map(x => x.partNumber === p.partNumber ? { ...x, state: 'in_flight', attempts: x.attempts + 1 } : x));
        const sentSize = (p.partNumber - 1) * CHUNK_SIZE;
        const chunk = file.slice(sentSize, sentSize + CHUNK_SIZE);
        try {
          const etag = await uploadPart(p, chunk, file.type);
          done.push({ partNumber: p.partNumber, etag });
          setParts(prev => prev.map(x => x.partNumber === p.partNumber ? { ...x, state: 'done', etag } : x));
        } catch (err) {
          setParts(prev => prev.map(x => x.partNumber === p.partNumber ? { ...x, state: 'failed' } : x));
          throw err;
        }
      }

      setPhase('completing');
      const complete = await UploadAPI.complete({
        mediaId,
        filename,
        fileType: file.type,
        uploadId: init.uploadId,
        mediaType,
        partETags: done,
      });

      sessionStorage.removeItem(resumeKey);
      setPhase('done');
      onComplete(complete.filename || filename);
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        setPhase('paused');
        return;
      }
      setError((err as Error).message || 'Upload failed');
      setPhase('error');
    }
  }

  function cancel() {
    abortRef.current?.abort();
  }

  function discardResume() {
    sessionStorage.removeItem(resumeKey);
    setResumeAvailable(null);
  }

  function copyUploadId() {
    if (uploadId) {
      navigator.clipboard.writeText(uploadId).catch(() => {});
    }
  }

  const progress = parts.length
    ? Math.round((parts.filter(p => p.state === 'done').length / parts.length) * 100)
    : 0;

  return (
    <div className="nb-up">
      <div className="nb-up__head">
        <div className="nb-up__title">Upload {mediaType.toLowerCase()}</div>
        {existingFilename && phase === 'idle' && (
          <div className="nb-up__current">Current: <code>{existingFilename}</code></div>
        )}
      </div>

      {resumeAvailable && phase === 'idle' && (
        <div className="nb-up__resume">
          <div>
            <strong>Unfinished upload found</strong>
            <p>{resumeAvailable.fileName} · {Math.ceil(resumeAvailable.fileSize / (1024 * 1024))} MB · upload id <code>{resumeAvailable.uploadId.slice(0, 12)}…</code></p>
          </div>
          <div className="nb-up__resume-actions">
            <Button onClick={discardResume}>Discard</Button>
            <Button tone="ember" onClick={() => setResumeAvailable(null)}>Continue with new file</Button>
          </div>
        </div>
      )}

      {phase === 'idle' && (
        <label
          className="nb-up__zone"
          onDragOver={e => e.preventDefault()}
          onDrop={onDrop}
        >
          <input
            type="file"
            accept={mediaType === 'AUDIO' ? 'audio/*' : 'video/*'}
            onChange={e => onPick(e.target.files?.[0])}
            hidden
          />
          {file ? (
            <div className="nb-up__pick">
              <div className="nb-up__pick-name">{file.name}</div>
              <div className="nb-up__pick-meta">
                {(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type || 'unknown type'} · {Math.ceil(file.size / CHUNK_SIZE)} parts
              </div>
            </div>
          ) : (
            <div className="nb-up__prompt">
              <strong>Drop a file</strong>
              <span>or click to pick · {mediaType.toLowerCase()} only</span>
            </div>
          )}
        </label>
      )}

      {(phase === 'uploading' || phase === 'completing' || phase === 'init') && (
        <div className="nb-up__progress">
          <div className="nb-up__progress-head">
            <span className="nb-up__progress-label">
              {phase === 'init' && 'Initiating…'}
              {phase === 'uploading' && `${progress}% · ${parts.filter(p => p.state === 'done').length} / ${parts.length} parts`}
              {phase === 'completing' && 'Finalizing…'}
            </span>
            {phase === 'uploading' && <Button onClick={cancel} tone="ghost">Cancel</Button>}
          </div>
          <div className="nb-up__bar"><div className="nb-up__bar-fill" style={{ width: progress + '%' }} /></div>
          <div className="nb-up__cells">
            {parts.map(p => (
              <span key={p.partNumber} className={`nb-up__cell nb-up__cell--${p.state}`} title={`Part ${p.partNumber} · ${p.state}${p.attempts > 1 ? ` · ${p.attempts} attempts` : ''}`} />
            ))}
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div className="nb-up__error">
          <div><strong>Upload failed.</strong> {error}</div>
          {uploadId && (
            <div className="nb-up__error-recovery">
              <span>Upload id: <code>{uploadId}</code></span>
              <Button onClick={copyUploadId} tone="ghost">Copy</Button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button onClick={() => { setPhase('idle'); setError(null); }}>Discard</Button>
            <Button tone="ember" onClick={start}>Retry</Button>
          </div>
        </div>
      )}

      {phase === 'paused' && (
        <div className="nb-up__paused">
          <strong>Cancelled.</strong>
          <Button tone="ember" onClick={() => setPhase('idle')}>Pick again</Button>
        </div>
      )}

      {phase === 'done' && (
        <div className="nb-up__done">
          <strong>Upload complete.</strong>
          <Button onClick={() => { setPhase('idle'); setFile(null); setParts([]); setUploadId(null); }}>Upload another</Button>
        </div>
      )}

      {file && phase === 'idle' && (
        <div className="nb-up__actions">
          <Button onClick={() => { setFile(null); fileRef.current = null; }} tone="ghost">Clear</Button>
          <Button onClick={start} tone="ember">
            {existingFilename ? 'Replace' : 'Start upload'}
          </Button>
        </div>
      )}
    </div>
  );
}

function sleep(ms: number) { return new Promise(r => setTimeout(r, ms)); }
